import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Clock, Eye, AlertTriangle, Flag, Plus, Mail, MapPin, Phone, Globe, Building2, Archive, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const AdminPortal = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  // Use authenticated user's ID for all admin API calls
  const adminId = user?.id;

  const [pendingCenters, setPendingCenters] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [flaggedRequests, setFlaggedRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState({ open: false, type: null, id: null });

  // Guard: redirect non-admin users
  if (!authLoading && (!isAuthenticated || user?.role !== 'admin')) {
    return <Navigate to="/" replace />;
  }
  const [showCreateCenterDialog, setShowCreateCenterDialog] = useState(false);
  const [newCenter, setNewCenter] = useState({
    name: '',
    type: 'hospital',
    country: '',
    city: '',
    state: '',
    zip_code: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    hours: '',
    walk_in: true,
    latitude: '',
    longitude: ''
  });
  
  // SOS Broadcast State
  const [showSosDialog, setShowSosDialog] = useState(false);
  const [sosData, setSosData] = useState({
    patient_name: '',
    blood_type: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    center_id: '',
    message: 'Urgent blood donation needed. Please contact the donation center if you can help.',
    blood_request_id: ''
  });
  const [donationCenters, setDonationCenters] = useState([]);

  useEffect(() => {
    fetchAllData();
    fetchDonationCenters();
  }, []);
  
  const fetchDonationCenters = async () => {
    try {
      const response = await axios.get(`${API}/donation-centers?status=approved`);
      setDonationCenters(response.data || []);
    } catch (error) {
      // Error handled silently - user will see empty list if fetch fails
    }
  };

  const fetchAllData = async () => {
    try {
      const [centersRes, requestsRes, flaggedRes] = await Promise.all([
        axios.get(`${API}/admin/pending-centers?admin_id=${adminId}`),
        axios.get(`${API}/admin/pending-requests?admin_id=${adminId}`),
        axios.get(`${API}/admin/blood-requests/flagged?admin_id=${adminId}`)
      ]);
      setPendingCenters(centersRes.data || []);
      setPendingRequests(requestsRes.data || []);
      setFlaggedRequests(flaggedRes.data || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load admin data. Please check your admin permissions.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApproveCenter = async (id) => {
    try {
      await axios.put(`${API}/admin/donation-centers/${id}/approve?admin_id=${adminId}`);
      toast({
        title: 'Center Approved',
        description: 'The donation center has been approved and is now visible to users.',
      });
      fetchAllData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to approve center. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleRejectCenter = async (id, reason) => {
    try {
      await axios.put(
        `${API}/admin/donation-centers/${id}/reject?admin_id=${adminId}`,
        null,
        { params: { reason } }
      );
      toast({
        title: 'Center Rejected',
        description: 'The donation center submission has been rejected.',
      });
      setShowRejectDialog({ open: false, type: null, id: null });
      setRejectionReason('');
      fetchAllData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to reject center. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleApproveRequest = async (id) => {
    try {
      await axios.put(`${API}/admin/blood-requests/${id}/approve?admin_id=${adminId}`);
      toast({
        title: 'Request Approved',
        description: 'The blood request has been approved and is now visible to donors.',
      });
      fetchAllData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to approve request. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleRejectRequest = async (id, reason) => {
    try {
      await axios.put(
        `${API}/admin/blood-requests/${id}/reject?admin_id=${adminId}`,
        null,
        { params: { reason } }
      );
      toast({
        title: 'Request Rejected',
        description: 'The blood request has been rejected.',
      });
      setShowRejectDialog({ open: false, type: null, id: null });
      setRejectionReason('');
      fetchAllData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to reject request. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleFlagRequest = async (id, reason) => {
    try {
      await axios.put(
        `${API}/admin/blood-requests/${id}/flag?admin_id=${adminId}`,
        null,
        { params: { reason } }
      );
      toast({
        title: 'Request Flagged',
        description: 'The blood request has been flagged for review.',
      });
      setShowRejectDialog({ open: false, type: null, id: null });
      setRejectionReason('');
      fetchAllData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to flag request. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleCreateCenter = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${API}/admin/donation-centers?user_id=${adminId}`,
        newCenter
      );
      toast({
        title: 'Center Created',
        description: 'The donation center has been created and is pending approval.',
      });
      setShowCreateCenterDialog(false);
      setNewCenter({
        name: '',
        type: 'hospital',
        country: '',
        city: '',
        state: '',
        zip_code: '',
        address: '',
        phone: '',
        email: '',
        website: '',
        hours: '',
        walk_in: true,
        latitude: '',
        longitude: ''
      });
      fetchAllData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create donation center. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleSosBroadcast = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${API}/admin/sos-broadcast?user_id=${adminId}`,
        sosData
      );
      toast({
        title: 'SOS Broadcast Sent',
        description: `SOS request sent to ${response.data.sent_count} users. ${response.data.failed_count} failed.`,
      });
      setShowSosDialog(false);
      setSosData({
        patient_name: '',
        blood_type: '',
        city: '',
        state: '',
        zip_code: '',
        country: '',
        center_id: '',
        message: 'Urgent blood donation needed. Please contact the donation center if you can help.',
        blood_request_id: ''
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to broadcast SOS request. Please try again.',
        variant: 'destructive',
      });
    }
  };


  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Admin Portal</h1>
          <p className="text-gray-700 text-lg">Manage pending submissions and approvals</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="shadow-md">
            <CardContent className="pt-6 text-center">
              <div className="text-4xl font-bold text-red-700 mb-2">{pendingCenters.length}</div>
              <p className="text-gray-700 font-medium">Pending Centers</p>
            </CardContent>
          </Card>
          <Card className="shadow-md">
            <CardContent className="pt-6 text-center">
              <div className="text-4xl font-bold text-red-700 mb-2">{pendingRequests.length}</div>
              <p className="text-gray-700 font-medium">Pending Requests</p>
            </CardContent>
          </Card>
          <Card className="shadow-md border-orange-300">
            <CardContent className="pt-6 text-center">
              <div className="text-4xl font-bold text-orange-700 mb-2">{flaggedRequests.length}</div>
              <p className="text-gray-700 font-medium">Flagged Requests</p>
            </CardContent>
          </Card>
          <Card className="shadow-md">
            <CardContent className="pt-6 text-center">
              <div className="text-4xl font-bold text-green-700 mb-2">0</div>
              <p className="text-gray-700 font-medium">Approved Today</p>
            </CardContent>
          </Card>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-700">Loading admin data...</p>
          </div>
        ) : (
          /* Tabs */
          <Tabs defaultValue="centers" className="space-y-6">
            <TabsList className="grid w-full grid-cols-5 bg-gray-200">
              <TabsTrigger value="centers" className="data-[state=active]:bg-red-700 data-[state=active]:text-white">
                Pending Centers ({pendingCenters.length})
              </TabsTrigger>
              <TabsTrigger value="create-center" className="data-[state=active]:bg-red-700 data-[state=active]:text-white">
                Create Center
              </TabsTrigger>
              <TabsTrigger value="requests" className="data-[state=active]:bg-red-700 data-[state=active]:text-white">
                Pending Requests ({pendingRequests.length})
              </TabsTrigger>
              <TabsTrigger value="flagged" className="data-[state=active]:bg-red-700 data-[state=active]:text-white">
                Flagged ({flaggedRequests.length})
              </TabsTrigger>
              <TabsTrigger value="sos" className="data-[state=active]:bg-red-700 data-[state=active]:text-white">
                SOS Broadcast
              </TabsTrigger>
            </TabsList>

          {/* Create Donation Center Tab */}
          <TabsContent value="create-center">
            <Card className="shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="h-5 w-5" />
                  Create New Donation Center
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateCenter} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="center-name">Center Name *</Label>
                      <Input
                        id="center-name"
                        value={newCenter.name}
                        onChange={(e) => setNewCenter({ ...newCenter, name: e.target.value })}
                        placeholder="Hospital or Blood Bank Name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="center-type">Type *</Label>
                      <Select
                        value={newCenter.type}
                        onValueChange={(value) => setNewCenter({ ...newCenter, type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hospital">Hospital</SelectItem>
                          <SelectItem value="blood_bank">Blood Bank</SelectItem>
                          <SelectItem value="clinic">Clinic</SelectItem>
                          <SelectItem value="mobile_unit">Mobile Unit</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="center-country">Country *</Label>
                      <Input
                        id="center-country"
                        value={newCenter.country}
                        onChange={(e) => setNewCenter({ ...newCenter, country: e.target.value })}
                        placeholder="United States"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="center-city">City *</Label>
                      <Input
                        id="center-city"
                        value={newCenter.city}
                        onChange={(e) => setNewCenter({ ...newCenter, city: e.target.value })}
                        placeholder="New York"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="center-state">State/Province</Label>
                      <Input
                        id="center-state"
                        value={newCenter.state}
                        onChange={(e) => setNewCenter({ ...newCenter, state: e.target.value })}
                        placeholder="New York"
                      />
                    </div>
                    <div>
                      <Label htmlFor="center-zip">Zip/Postal Code</Label>
                      <Input
                        id="center-zip"
                        value={newCenter.zip_code}
                        onChange={(e) => setNewCenter({ ...newCenter, zip_code: e.target.value })}
                        placeholder="10001"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="center-address">Address *</Label>
                      <Input
                        id="center-address"
                        value={newCenter.address}
                        onChange={(e) => setNewCenter({ ...newCenter, address: e.target.value })}
                        placeholder="123 Main Street"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="center-phone">Phone *</Label>
                      <Input
                        id="center-phone"
                        type="tel"
                        value={newCenter.phone}
                        onChange={(e) => setNewCenter({ ...newCenter, phone: e.target.value })}
                        placeholder="+1 555-123-4567"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="center-email">Email *</Label>
                      <Input
                        id="center-email"
                        type="email"
                        value={newCenter.email}
                        onChange={(e) => setNewCenter({ ...newCenter, email: e.target.value })}
                        placeholder="contact@center.com"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="center-website">Website</Label>
                      <Input
                        id="center-website"
                        type="url"
                        value={newCenter.website}
                        onChange={(e) => setNewCenter({ ...newCenter, website: e.target.value })}
                        placeholder="https://www.center.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="center-hours">Hours</Label>
                      <Input
                        id="center-hours"
                        value={newCenter.hours}
                        onChange={(e) => setNewCenter({ ...newCenter, hours: e.target.value })}
                        placeholder="Mon-Fri: 9AM-5PM"
                      />
                    </div>
                    <div>
                      <Label htmlFor="center-lat">Latitude</Label>
                      <Input
                        id="center-lat"
                        type="number"
                        step="any"
                        value={newCenter.latitude}
                        onChange={(e) => setNewCenter({ ...newCenter, latitude: e.target.value })}
                        placeholder="40.7128"
                      />
                    </div>
                    <div>
                      <Label htmlFor="center-lng">Longitude</Label>
                      <Input
                        id="center-lng"
                        type="number"
                        step="any"
                        value={newCenter.longitude}
                        onChange={(e) => setNewCenter({ ...newCenter, longitude: e.target.value })}
                        placeholder="-74.0060"
                      />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="center-walk-in"
                      checked={newCenter.walk_in}
                      onChange={(e) => setNewCenter({ ...newCenter, walk_in: e.target.checked })}
                      className="rounded"
                    />
                    <Label htmlFor="center-walk-in" className="cursor-pointer">
                      Accepts walk-in donations
                    </Label>
                  </div>
                  <Button type="submit" className="bg-red-700 hover:bg-red-800 text-white">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Donation Center
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Donation Centers Tab */}
          <TabsContent value="centers">
            <div className="space-y-4">
              {pendingCenters.map((center) => (
                <Card key={center.id} className="shadow-md">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-gray-900">{center.name}</CardTitle>
                        <div className="flex items-center space-x-2 mt-2">
                          <Badge className="bg-blue-700 text-white">{center.type}</Badge>
                          <Badge variant="outline" className="text-orange-700 border-orange-700">
                            <Clock className="h-3 w-3 mr-1" />
                            Pending
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-700 font-semibold">Location</p>
                        <p className="text-gray-900">{center.city}, {center.country}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-700 font-semibold">Phone</p>
                        <p className="text-gray-900">{center.phone}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-700 font-semibold">Email</p>
                        <p className="text-gray-900">{center.email}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-700 font-semibold">Submitted By</p>
                        <p className="text-gray-900">{center.submittedBy}</p>
                      </div>
                    </div>
                    <div className="mb-4">
                      <p className="text-sm text-gray-700 font-semibold mb-1">Address</p>
                      <p className="text-gray-900">{center.address}</p>
                    </div>
                    <p className="text-xs text-gray-600 mb-4">
                      Submitted on {new Date(center.submittedDate).toLocaleDateString()}
                    </p>
                    <div className="flex space-x-3">
                      <Button
                        onClick={() => handleApproveCenter(center.id)}
                        className="bg-green-700 hover:bg-green-800 text-white btn-transition"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => setShowRejectDialog({ open: true, type: 'center', id: center.id })}
                        variant="outline"
                        className="text-red-700 border-red-700 hover:bg-red-50"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {pendingCenters.length === 0 && (
                <Card className="shadow-md">
                  <CardContent className="pt-6 text-center">
                    <p className="text-gray-700">No pending donation centers to review.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Blood Requests Tab */}
          <TabsContent value="requests">
            <div className="space-y-4">
              {pendingRequests.map((request) => (
                <Card key={request.id} className="shadow-md">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-gray-900">{request.hospital}</CardTitle>
                        <p className="text-gray-700">{request.city}, {request.country}</p>
                      </div>
                      <Badge className="bg-red-700 text-white text-lg">{request.bloodType}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-700 font-semibold">Patient</p>
                        <p className="text-gray-900">{request.patientName}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-700 font-semibold">Units Needed</p>
                        <p className="text-gray-900">{request.unitsNeeded} units</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-700 font-semibold">Urgency</p>
                        <Badge className={
                          request.urgency === 'critical' ? 'bg-red-700 text-white' :
                          request.urgency === 'high' ? 'bg-orange-600 text-white' :
                          'bg-yellow-600 text-white'
                        }>
                          {request.urgency.toUpperCase()}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-sm text-gray-700 font-semibold">Submitted By</p>
                        <p className="text-gray-900">{request.submittedBy}</p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mb-4">
                      Submitted on {new Date(request.submittedDate).toLocaleDateString()}
                    </p>
                    <div className="flex space-x-3">
                      <Button
                        onClick={() => handleApproveRequest(request.id)}
                        className="bg-green-700 hover:bg-green-800 text-white btn-transition"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => setShowRejectDialog({ open: true, type: 'request', id: request.id })}
                        variant="outline"
                        className="text-red-700 border-red-700 hover:bg-red-50"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {pendingRequests.length === 0 && (
                <Card className="shadow-md">
                  <CardContent className="pt-6 text-center">
                    <p className="text-gray-700">No pending blood requests to review.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Flagged Requests Tab */}
          <TabsContent value="flagged">
            <div className="space-y-4">
              {flaggedRequests.map((request) => (
                <Card key={request.id} className="shadow-md border-orange-300">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-gray-900 flex items-center gap-2">
                          <Flag className="h-5 w-5 text-orange-600" />
                          {request.hospital || 'Blood Request'}
                        </CardTitle>
                        <p className="text-gray-700">{request.city}, {request.country}</p>
                      </div>
                      <Badge className="bg-orange-600 text-white text-lg flex items-center gap-1">
                        <AlertTriangle className="h-4 w-4" />
                        FLAGGED
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {request.flag_reason && (
                      <div className="mb-4 p-3 bg-orange-50 rounded-lg border border-orange-200">
                        <p className="text-sm text-gray-700 font-semibold mb-1">Flag Reason:</p>
                        <p className="text-gray-900 text-sm">{request.flag_reason}</p>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-700 font-semibold">Blood Type</p>
                        <Badge className="bg-red-700 text-white text-lg mt-1">{request.blood_type}</Badge>
                      </div>
                      <div>
                        <p className="text-sm text-gray-700 font-semibold">Units Needed</p>
                        <p className="text-gray-900">{request.units_needed || request.unitsNeeded} units</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-700 font-semibold">Urgency</p>
                        <Badge className={
                          request.urgency === 'critical' ? 'bg-red-700 text-white' :
                          request.urgency === 'high' ? 'bg-orange-600 text-white' :
                          'bg-yellow-600 text-white'
                        }>
                          {request.urgency?.toUpperCase() || 'MEDIUM'}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-sm text-gray-700 font-semibold">Status</p>
                        <Badge className="bg-orange-600 text-white">FLAGGED</Badge>
                      </div>
                      {request.patient_name && (
                        <div>
                          <p className="text-sm text-gray-700 font-semibold">Patient</p>
                          <p className="text-gray-900">{request.patient_name}</p>
                        </div>
                      )}
                      {request.contact_phone && (
                        <div>
                          <p className="text-sm text-gray-700 font-semibold">Contact</p>
                          <p className="text-gray-900">{request.contact_phone}</p>
                        </div>
                      )}
                    </div>
                    {request.description && (
                      <div className="mb-4">
                        <p className="text-sm text-gray-700 font-semibold mb-1">Description</p>
                        <p className="text-gray-900 text-sm">{request.description}</p>
                      </div>
                    )}
                    <p className="text-xs text-gray-600 mb-4">
                      Flagged on {request.updated_at ? new Date(request.updated_at).toLocaleDateString() : 'Unknown date'}
                    </p>
                    <div className="flex space-x-3">
                      <Button
                        onClick={() => handleApproveRequest(request.id)}
                        className="bg-green-700 hover:bg-green-800 text-white btn-transition"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Approve & Activate
                      </Button>
                      <Button
                        onClick={() => setShowRejectDialog({ open: true, type: 'flagged-request', id: request.id })}
                        variant="outline"
                        className="text-red-700 border-red-700 hover:bg-red-50"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {flaggedRequests.length === 0 && (
                <Card className="shadow-md">
                  <CardContent className="pt-6 text-center">
                    <p className="text-gray-700">No flagged blood requests to review.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* SOS Broadcast Tab */}
          <TabsContent value="sos">
            <Card className="shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Broadcast SOS Blood Request
                </CardTitle>
                <p className="text-sm text-gray-600 mt-2">
                  Send urgent blood donation requests to all opt-in users in a specific location.
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSosBroadcast} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="sos-patient">Patient Name *</Label>
                      <Input
                        id="sos-patient"
                        value={sosData.patient_name}
                        onChange={(e) => setSosData({ ...sosData, patient_name: e.target.value })}
                        placeholder="John Doe"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="sos-blood-type">Blood Type Needed *</Label>
                      <Select
                        value={sosData.blood_type}
                        onValueChange={(value) => setSosData({ ...sosData, blood_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select blood type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="A+">A+</SelectItem>
                          <SelectItem value="A-">A-</SelectItem>
                          <SelectItem value="B+">B+</SelectItem>
                          <SelectItem value="B-">B-</SelectItem>
                          <SelectItem value="AB+">AB+</SelectItem>
                          <SelectItem value="AB-">AB-</SelectItem>
                          <SelectItem value="O+">O+</SelectItem>
                          <SelectItem value="O-">O-</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="sos-country">Country *</Label>
                      <Input
                        id="sos-country"
                        value={sosData.country}
                        onChange={(e) => setSosData({ ...sosData, country: e.target.value })}
                        placeholder="United States"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="sos-city">City</Label>
                      <Input
                        id="sos-city"
                        value={sosData.city}
                        onChange={(e) => setSosData({ ...sosData, city: e.target.value })}
                        placeholder="New York"
                      />
                    </div>
                    <div>
                      <Label htmlFor="sos-state">State/Province</Label>
                      <Input
                        id="sos-state"
                        value={sosData.state}
                        onChange={(e) => setSosData({ ...sosData, state: e.target.value })}
                        placeholder="New York"
                      />
                    </div>
                    <div>
                      <Label htmlFor="sos-zip">Zip/Postal Code</Label>
                      <Input
                        id="sos-zip"
                        value={sosData.zip_code}
                        onChange={(e) => setSosData({ ...sosData, zip_code: e.target.value })}
                        placeholder="10001"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="sos-center">Donation Center (Optional)</Label>
                      <Select
                        value={sosData.center_id}
                        onValueChange={(value) => setSosData({ ...sosData, center_id: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select donation center (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">None</SelectItem>
                          {donationCenters.map((center) => (
                            <SelectItem key={center.id} value={center.id}>
                              {center.name} - {center.city}, {center.country}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="sos-message">Custom Message</Label>
                      <Textarea
                        id="sos-message"
                        value={sosData.message}
                        onChange={(e) => setSosData({ ...sosData, message: e.target.value })}
                        placeholder="Urgent blood donation needed..."
                        rows={4}
                      />
                    </div>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      <strong>Note:</strong> This will send an email to all users who have opted in for SOS alerts in the specified location. 
                      At least one location filter (country, city, state, or zip code) must be provided.
                    </p>
                  </div>
                  <Button type="submit" className="bg-red-700 hover:bg-red-800 text-white">
                    <Mail className="h-4 w-4 mr-2" />
                    Broadcast SOS Request
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        )}

        {/* Rejection Dialog */}
        <Dialog open={showRejectDialog.open} onOpenChange={(open) => setShowRejectDialog({ ...showRejectDialog, open })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject {showRejectDialog.type === 'center' ? 'Donation Center' : 'Blood Request'}</DialogTitle>
              <DialogDescription>
                Please provide a reason for rejection. This will be recorded for audit purposes.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="rejection-reason">Rejection Reason</Label>
                <Textarea
                  id="rejection-reason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter reason for rejection..."
                  rows={4}
                  className="border-gray-300"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRejectDialog({ open: false, type: null, id: null });
                    setRejectionReason('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className="bg-red-700 hover:bg-red-900 text-white"
                  onClick={() => {
                    if (showRejectDialog.type === 'center') {
                      handleRejectCenter(showRejectDialog.id, rejectionReason);
                    } else {
                      handleRejectRequest(showRejectDialog.id, rejectionReason);
                    }
                  }}
                >
                  Confirm Rejection
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminPortal;